/** Accounts + cloud progress sync (versioned, merge on conflict, offline queue). */
import { create } from 'zustand';
import { emptyDoc, fitDoc, mergeDocs, type ProgressDoc } from '../../shared/progress';
import { settingsSnapshot, useSettings, type Settings } from '../store/settings';
import { api, ApiError } from './api';
import { saveHistory, setHistoryNamespace, useGame } from './game';
import { load, save } from './storage';

export interface User { id: number; username: string; name: string }
type Sync = 'local' | 'synced' | 'syncing' | 'pending' | 'offline' | 'error';
export const useAuth = create<{ user: User | null; sync: Sync; syncError: string; version: number; checked: boolean }>(() => ({ user: null, sync: 'local', syncError: '', version: 0, checked: false }));

export async function refreshMe() {
  try { const r = await api<{ user: User | null }>('GET', '/api/auth/me'); await setUser(r.user); }
  catch { useAuth.setState({ sync: 'offline' }); }
  useAuth.setState({ checked: true });
}
async function setUser(u: User | null) {
  const prev = useAuth.getState().user;
  if (prev?.id !== u?.id) {
    // switch namespaces so one account's cloud history never shows under another account
    setHistoryNamespace(u ? `u${u.id}` : 'anon');
    useAuth.setState({ version: load<number>(`ver.u${u?.id}`, 0) });
  }
  useAuth.setState({ user: u, sync: u ? 'pending' : 'local' });
  if (u) await syncNow();
}
export async function login(username: string, password: string, register: boolean) {
  const r = await api<{ user: User }>('POST', register ? '/api/auth/register' : '/api/auth/login', { username, password });
  await setUser(r.user);
}
export async function logout() { await api('POST', '/api/auth/logout').catch(() => null); await setUser(null); }
export async function rename(name: string) { const r = await api<{ user: User }>('POST', '/api/auth/name', { name }); useAuth.setState({ user: r.user }); }

/** Import anonymous local results into the signed-in account (explicit user action only). */
export function importAnon(): number {
  const anon = load<ProgressDoc['history']>('history.anon', []);
  const cur = useGame.getState().history; const ids = new Set(cur.map((x) => x.id));
  const add = anon.filter((x) => !ids.has(x.id)); if (!add.length) return 0;
  saveHistory([...cur, ...add].sort((a, b) => a.at - b.at)); schedule(); return add.length;
}

function localDoc(): ProgressDoc { const s = useSettings.getState(); return { v: 1, history: useGame.getState().history, settings: settingsSnapshot(s) as unknown as Record<string, unknown>, settingsAt: s.settingsAt }; }
function applyDoc(d: ProgressDoc) {
  saveHistory(d.history);
  const s = useSettings.getState(); if (d.settingsAt > s.settingsAt) s.replace(d.settings as Partial<Settings>, d.settingsAt);
}

let busy = false, again = false, timer: number | null = null;
export function schedule() { if (!useAuth.getState().user) return; useAuth.setState({ sync: 'pending' }); if (timer) clearTimeout(timer); timer = window.setTimeout(() => void syncNow(), 1500); }
export async function syncNow(): Promise<void> {
  const u = useAuth.getState().user; if (!u) return;
  if (busy) { again = true; return; }
  if (!navigator.onLine) { useAuth.setState({ sync: 'offline' }); return; }
  busy = true; useAuth.setState({ sync: 'syncing' });
  try {
    const remote = await api<{ version: number; doc: ProgressDoc | null }>('GET', '/api/progress');
    if (useAuth.getState().user?.id !== u.id) return;
    let version = remote.version; let doc = fitDoc(mergeDocs(localDoc(), remote.doc ?? emptyDoc()));
    for (let i = 0; i < 4; i++) {
      try { const r = await api<{ version: number }>('PUT', '/api/progress', { version, doc }); version = r.version; break; }
      catch (e) {
        if (e instanceof ApiError && e.status === 409) { version = Number(e.body.version); doc = fitDoc(mergeDocs(doc, (e.body.doc as ProgressDoc) ?? emptyDoc())); continue; }
        throw e;
      }
    }
    if (useAuth.getState().user?.id !== u.id) return;
    applyDoc(doc); save(`ver.u${u.id}`, version);
    useAuth.setState({ sync: 'synced', version, syncError: '' });
  } catch (e) {
    useAuth.setState({ sync: e instanceof ApiError ? 'error' : 'offline', syncError: String((e as Error).message) });
  } finally { busy = false; if (again) { again = false; void syncNow(); } }
}
window.addEventListener('online', () => void syncNow());
useGame.subscribe((s, p) => { if (s.history !== p.history && s.ns === p.ns && useAuth.getState().sync !== 'syncing') schedule(); });
useSettings.subscribe((s, p) => { if (s.settingsAt !== p.settingsAt) schedule(); });
